import React from 'react';
import { View, Text, StyleSheet, Pressable, Platform, useWindowDimensions } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Brand, Colors, Fonts, Radii, Spacing } from '../constants';
import { LAYOUT } from '../constants/layout';
import { useUnreadCountContext, useUser } from '../contexts';
import { Button } from './Button';
import { BrandLogo } from './BrandLogo';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { goToSection } from '../utils/navigation';
import type { RootStackParamList } from '../types';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface NavItem {
  screen: 'Home' | 'Browse' | 'ConversationsList' | 'Clubs' | 'Profile';
  icon: keyof typeof Ionicons.glyphMap;
  iconActive: keyof typeof Ionicons.glyphMap;
  translationKey: string;
}

const NAV_ITEMS: NavItem[] = [
  { screen: 'Home', icon: 'home-outline', iconActive: 'home', translationKey: 'navigation.home' },
  { screen: 'Browse', icon: 'search-outline', iconActive: 'search', translationKey: 'navigation.browse' },
  { screen: 'ConversationsList', icon: 'chatbubbles-outline', iconActive: 'chatbubbles', translationKey: 'navigation.conversations' },
  { screen: 'Clubs', icon: 'people-outline', iconActive: 'people', translationKey: 'navigation.clubs' },
  { screen: 'Profile', icon: 'person-outline', iconActive: 'person', translationKey: 'navigation.profile' },
];

/**
 * Desktop navbar (design v2): fixed 64px Bosgroen bar, no border. Links are
 * Poppins medium 15 in saliemist, turn white on hover, and the active link is
 * white with a 2px accent underline. Unread messages show as an accent-filled
 * count pill.
 */
export const Navbar = React.memo(() => {
  const navigation = useNavigation<NavigationProp>();
  const { t } = useTranslation();
  const { user } = useUser();
  const { unreadCount } = useUnreadCountContext();
  const currentRoute = useNavigationState(state =>
    state?.routes[state.index]?.name as keyof RootStackParamList
  );
  // Below ~960px (tablet) the five labelled links plus the button don't fit
  // in the 1200px grid, so links collapse to icons with accessible labels.
  const { width } = useWindowDimensions();
  const compact = width < 960;

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Pressable
          style={styles.logoContainer}
          onPress={() => (user ? goToSection(navigation, 'Home') : goToSection(navigation, 'Browse', {}))}
          accessibilityLabel={user ? t('navigation.home') : t('navigation.browse')}
          accessibilityRole="button"
        >
          <BrandLogo height={LAYOUT.navbar.desktop} />
        </Pressable>

        <View style={styles.nav}>
          {user ? (
            // Authenticated nav
            <>
              {NAV_ITEMS.map((item) => {
                const isActive = currentRoute === item.screen;
                const showUnread = item.screen === 'ConversationsList' && unreadCount > 0;
                return (
                  <Pressable
                    key={item.screen}
                    style={[styles.link, compact && styles.linkCompact]}
                    accessibilityRole="button"
                    accessibilityLabel={t(item.translationKey)}
                    accessibilityState={{ selected: isActive }}
                    onPress={() => {
                      if (item.screen === 'Browse') {
                        goToSection(navigation, 'Browse', {});
                      } else {
                        goToSection(navigation, item.screen);
                      }
                    }}
                  >
                    {({ hovered }: any) => {
                      const color = isActive || hovered ? Colors.white : Brand.saliemist;
                      return (
                        <>
                          <View style={styles.iconContainer}>
                            <Ionicons
                              name={isActive ? item.iconActive : item.icon}
                              size={19}
                              color={color}
                            />
                            {showUnread && (
                              <View style={styles.unreadBadge}>
                                <Text style={styles.unreadBadgeText} numberOfLines={1}>
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </Text>
                              </View>
                            )}
                          </View>
                          {!compact && <Text style={[styles.linkText, { color }]}>{t(item.translationKey)}</Text>}
                          <View style={[styles.underline, isActive && styles.underlineActive]} />
                        </>
                      );
                    }}
                  </Pressable>
                );
              })}
              <Button
                title={compact ? t('common.buttons.create') : t('navigation.addListing')}
                icon="add"
                onPress={() => navigation.navigate('ItemForm')}
                style={styles.addButton}
                testID="navbar-add-listing"
              />
            </>
          ) : (
            // Guest nav — Log in + Register buttons
            <>
              {/* `dark` fills with Bosgroen — invisible against the bar — so this
                  reads as the kit's outline button: white label on a 50% white
                  hairline. */}
              <Button
                title={t('navigation.login')}
                variant="dark"
                onPress={() => navigation.navigate('Login')}
                style={styles.guestButton}
              />
              <Button
                title={t('navigation.register')}
                onPress={() => navigation.navigate('Register')}
                style={styles.guestPrimaryButton}
              />
            </>
          )}
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    height: LAYOUT.navbar.desktop,
    backgroundColor: Brand.bosgroen,
    flexDirection: 'row',
    alignItems: 'center',
    // Centre the inner content so the logo lines up with the page's
    // max-width content container instead of sitting flush against the edge.
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      // Prevent subpixel rendering shifts
      WebkitFontSmoothing: 'antialiased' as any,
      backfaceVisibility: 'hidden' as any,
      transform: 'translateZ(0)' as any,
      willChange: 'transform' as any,
    }),
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: LAYOUT.maxContentWidth,
    paddingHorizontal: LAYOUT.contentPadding.desktop,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  },
  nav: {
    flexDirection: 'row',
    gap: Spacing.xs,
    alignItems: 'center',
  },
  link: {
    height: LAYOUT.navbar.desktop,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    // 12 + the 4px nav gap = the 28px rhythm between links in the spec.
    paddingHorizontal: Spacing.md,
    position: 'relative',
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  linkCompact: {
    paddingHorizontal: Spacing.md,
  },
  iconContainer: {
    width: 19,
    height: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadBadge: {
    position: 'absolute',
    top: -7,
    right: -9,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 5,
    borderRadius: Radii.full, // round by nature — an unread count
    backgroundColor: Colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadBadgeText: {
    fontFamily: Fonts.bold,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.white,
  },
  linkText: {
    fontFamily: Fonts.medium,
    fontSize: 15,
    lineHeight: 18,
  },
  underline: {
    position: 'absolute',
    left: Spacing.md,
    right: Spacing.md,
    bottom: 0,
    height: 2,
    borderRadius: 2,
    backgroundColor: Colors.primary,
    transform: [{ scaleX: 0 }],
    ...(Platform.OS === 'web' && { transitionDuration: '150ms', transitionProperty: 'transform' } as any),
  },
  underlineActive: {
    transform: [{ scaleX: 1 }],
  },
  addButton: {
    marginLeft: Spacing.base,
    height: 40,
    borderRadius: Radii.control,
    paddingHorizontal: Spacing.base,
  },
  guestButton: {
    height: 40,
    borderRadius: Radii.control,
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  guestPrimaryButton: {
    height: 40,
    borderRadius: Radii.control,
    marginLeft: Spacing.sm,
  },
});
